// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData, Station } from '../types';
import { histogram } from '../analysis';
import { sparkline } from '../charts';
import { rateColor } from '../color';
import { fmtRate, fmtSigned, fmtNum, fmtTemp, esc, attr } from '../format';
import { gloss } from '../glossary';
import { openDrawer } from '../drawer';
import { sectionHead } from './ui';

type SortKey = 'name' | 'state' | 'rateMean' | 'rateMin' | 'rateMax' | 'recentAnom' | 'latestH35' | 'span';
const HMIN = -0.05, HMAX = 0.35, HBINS = 8;

export function mount(el: HTMLElement, data: ClimateData): void {
  let q = '';
  let sortKey: SortKey = 'rateMean';
  let sortDir: 1 | -1 = -1;
  let binFilter: [number, number] | null = null;

  el.innerHTML = `
    <section class="panel">
      ${sectionHead('Explore all 112 stations', `Search, sort and open any ${gloss('station', 'reference station')}. Click a bar in the histogram to filter the table by ${gloss('rate', 'warming rate')}.`)}
      <div class="hist-wrap"><div class="hist" data-hist></div></div>
      <div class="controls">
        <input type="search" class="search" placeholder="Search station or state…" data-q aria-label="Search stations" />
        <span class="filter-pill" data-pill hidden></span>
      </div>
      <div class="table-scroll">
        <table class="data-table">
          <thead><tr>
            ${th('name', 'Station')}${th('state', 'State')}${th('span', 'Record')}
            ${th('rateMean', 'Warming')}${th('rateMin', 'Nights')}${th('rateMax', 'Days')}
            ${th('recentAnom', 'Recent')}${th('latestH35', 'Hot days')}
            <th>Hottest day</th><th>Trend</th>
          </tr></thead>
          <tbody data-body></tbody>
        </table>
      </div>
    </section>`;

  const histEl = el.querySelector('[data-hist]') as HTMLElement;
  const bodyEl = el.querySelector('[data-body]') as HTMLElement;
  const pill = el.querySelector('[data-pill]') as HTMLElement;

  const renderHist = () => {
    const rates = data.stations.map((s) => s.rateMean).filter((v): v is number => v != null);
    const bins = histogram(rates, HMIN, HMAX, HBINS);
    const maxC = Math.max(...bins.map((b) => b.count), 1);
    const W = 1000, H = 140, m = { t: 8, b: 26, l: 8, r: 8 };
    const iw = W - m.l - m.r, ih = H - m.t - m.b;
    const bw = iw / bins.length;
    let g = '';
    bins.forEach((b, i) => {
      const active = binFilter && Math.abs(binFilter[0] - b.x0) < 1e-6;
      const h = (b.count / maxC) * ih;
      const midColor = rateColor((b.x0 + b.x1) / 2, 0.3);
      g += `<rect x="${(m.l + i * bw + 2).toFixed(1)}" y="${(m.t + ih - h).toFixed(1)}" width="${(bw - 4).toFixed(1)}" height="${h.toFixed(1)}" rx="2" fill="${midColor}" class="hist-bar ${active ? 'active' : ''}" data-bin="${i}" data-tip="${attr(`${b.x0.toFixed(2)} to ${b.x1.toFixed(2)} °C/decade: ${b.count} stations`)}"/>`;
      g += `<text x="${(m.l + i * bw + bw / 2).toFixed(1)}" y="${H - 8}" class="axis-lbl" text-anchor="middle">${b.x0.toFixed(2)}</text>`;
    });
    histEl.innerHTML = `<svg viewBox="0 0 ${W} ${H}" class="hist-svg" role="img" aria-label="Warming-rate histogram">${g}</svg>`;
  };

  const passesFilter = (s: Station) => {
    if (q) {
      const hay = `${s.name} ${s.state}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (binFilter) {
      if (s.rateMean == null) return false;
      const [lo, hi] = binFilter;
      if (s.rateMean < lo || s.rateMean >= hi) return false;
    }
    return true;
  };

  const val = (s: Station, k: SortKey): number | string => {
    switch (k) {
      case 'name': return s.name;
      case 'state': return s.state;
      case 'span': return s.startYear ?? 0;
      default: return (s[k] as number | null) ?? -Infinity;
    }
  };

  const renderBody = () => {
    let rows = data.stations.filter(passesFilter);
    rows = rows.sort((a, b) => {
      const va = val(a, sortKey), vb = val(b, sortKey);
      if (typeof va === 'string' && typeof vb === 'string') return va.localeCompare(vb) * sortDir;
      return ((va as number) - (vb as number)) * sortDir;
    });
    bodyEl.innerHTML = rows.length ? rows.map((s) => row(s)).join('') :
      `<tr><td colspan="10" class="muted" style="padding:20px;text-align:center">No stations match.</td></tr>`;
    pill.hidden = !binFilter;
    if (binFilter) pill.innerHTML = `Warming ${binFilter[0].toFixed(2)}–${binFilter[1].toFixed(2)} °C/dec <button data-clear aria-label="Clear filter">Clear ✕</button>`;
  };

  histEl.addEventListener('click', (e) => {
    const bar = (e.target as Element).closest('[data-bin]') as HTMLElement | null;
    if (!bar) return;
    const i = parseInt(bar.getAttribute('data-bin')!, 10);
    const w = (HMAX - HMIN) / HBINS;
    const lo = HMIN + i * w, hi = HMIN + (i + 1) * w;
    binFilter = binFilter && Math.abs(binFilter[0] - lo) < 1e-6 ? null : [lo, hi];
    renderHist(); renderBody();
  });
  el.querySelector('[data-q]')?.addEventListener('input', debounce((e: Event) => {
    q = (e.target as HTMLInputElement).value.trim().toLowerCase(); renderBody();
  }, 250));
  el.addEventListener('click', (e) => {
    if ((e.target as Element).closest('[data-clear]')) { binFilter = null; renderHist(); renderBody(); return; }
    const th = (e.target as Element).closest('th[data-sort]') as HTMLElement | null;
    if (th) {
      const k = th.getAttribute('data-sort') as SortKey;
      if (k === sortKey) sortDir = (sortDir === 1 ? -1 : 1); else { sortKey = k; sortDir = k === 'name' || k === 'state' ? 1 : -1; }
      el.querySelectorAll('th[data-sort]').forEach((x) => x.classList.remove('sorted-asc', 'sorted-desc'));
      th.classList.add(sortDir === 1 ? 'sorted-asc' : 'sorted-desc');
      renderBody();
      return;
    }
    const r = (e.target as Element).closest('tr[data-station]') as HTMLElement | null;
    if (r) openDrawer(data, r.getAttribute('data-station')!);
  });

  renderHist();
  renderBody();
}

function th(key: string, label: string): string {
  return `<th data-sort="${key}" class="sortable">${label}<span class="sort-caret"></span></th>`;
}

function row(s: Station): string {
  const spark = sparkline(s.annual.map((r) => r.aMean), { w: 90, h: 22, color: rateColor(s.rateMean, 0.3) });
  return `<tr data-station="${s.id}" tabindex="0">
    <td class="td-name">${esc(s.name)} ${spark}</td>
    <td>${s.state}</td>
    <td class="mono">${s.startYear}–${String(s.endYear).slice(2)}</td>
    <td class="mono" style="color:${rateColor(s.rateMean, 0.3)};font-weight:600">${fmtRate(s.rateMean)}</td>
    <td class="mono">${fmtRate(s.rateMin)}</td>
    <td class="mono">${fmtRate(s.rateMax)}</td>
    <td class="mono">${s.recentAnom != null ? fmtSigned(s.recentAnom) : '—'}</td>
    <td class="mono">${s.latestH35 != null ? fmtNum(s.latestH35) : '—'}</td>
    <td class="mono">${s.recHigh ? fmtTemp(s.recHigh.t) : '—'}</td>
    <td>${s.trendH35 != null ? `<span class="mono">${fmtSigned(s.trendH35, 1, '')}d/dec</span>` : '—'}</td>
  </tr>`;
}

function debounce<T extends (...a: never[]) => void>(fn: T, ms: number): T {
  let t: ReturnType<typeof setTimeout>;
  return ((...a: never[]) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }) as T;
}
